/* eslint-disable @typescript-eslint/no-unsafe-return,@typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-argument,@typescript-eslint/no-unsafe-assignment */
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse, CreateAxiosDefaults } from "axios";
import { TaJson } from "ta-json";

import OAuthClientCredentialsGrant from "../authentication/oauth-client-credentials-grant";
import OAuthPasswordGrant from "../authentication/oauth-password-grant";
import OAuthRefreshTokenGrant from "../authentication/oauth-refreshtoken-grant";
import { TokenInfo } from "../authentication/token-info";
import { MapStringTo, Nullable } from "../base-types";
import { AuthenticationError } from "../errors/authentication-error";
import { InvalidOperationError } from "../errors/invalid-operation-error";
import Guard from "../guard";
import { HttpMethod } from "../http-method";
import { RequestMessage } from "./request-message";
import { ResponseMessage } from "./response-message";

export type CancelCallback = (cancel: (reason?: string) => void) => void;

export interface IInternalClient {
    /**
     * Authenticate with the remote server.
     *
     * @remarks
     * Should return true when authentication succeeded.
     * Should return false or throw an error when authentication failed.
     *
     * @returns Promise resolving to a boolean indicating if authentication succeeded.
     */
    authenticateAsync(): Promise<boolean>;

    /**
     * Sends a request to the remote server and returns the server's response.
     * @param requestGenerator - Function which returns a {@link RequestMessage}
     * @param cancelCallback - A {@link CancelCallback} that will be placed in an axios {@link CancelToken} if provided
     */
    sendAsync<T = unknown, K = unknown>(
        requestGenerator: () => RequestMessage<T>,
        cancelCallback?: CancelCallback
    ): Promise<ResponseMessage<K>>;

    /**
     * Set headers which will be send with every request.
     * @param headers - The headers to send with every request
     */
    setRequestHeaders(headers: MapStringTo<string>): void;

    /**
     * Sets the error response handler.
     * @param onError - The callback function to handle the response error.
     *
     * @remarks
     * Only expected to work when authentication to the endpoint has been established without the use of an OAuth grant.
     */
    setResponseErrorHandler(onError: (error: any) => Promise<any>): void;
}

export class InternalClient implements IInternalClient {
    private _accessToken: string;
    private _passwordGrant: Nullable<OAuthPasswordGrant> = null;
    private _refreshTokenGrant: Nullable<OAuthRefreshTokenGrant> = null;
    private _clientCredentialsGrant: Nullable<OAuthClientCredentialsGrant> = null;
    private _isAuthenticated: boolean;
    private _requestHeaders: MapStringTo<string> = {};
    private readonly _client: AxiosInstance;

    /**
     * Indicates if we should authenticate with OAuth.
     */
    private get shouldAuthenticateWithOAuth(): boolean {
        return !!(this._passwordGrant || this._refreshTokenGrant || this._clientCredentialsGrant);
    }

    constructor(
        baseUri: URI | string,
        oauthGrant?: OAuthPasswordGrant | OAuthRefreshTokenGrant | OAuthClientCredentialsGrant
    ) {
        Guard.notNullOrUndefined(baseUri);
        Guard.notNull(oauthGrant);

        const baseUrl = typeof baseUri === "string" ? baseUri : baseUri.href();
        Guard.stringNotNullOrEmpty(baseUrl);

        const createClientParameters: CreateAxiosDefaults = {
            baseURL: baseUrl,
            withCredentials: true,
        };

        // This setting should be added to the .env file for connecting to a server using a self signed certificate
        if (typeof process !== "undefined" && process != null) {
            if (process.env.IGNORE_HTTPS_ERRORS === "true") {
                // Esbuild doesn't like importing node modules when building for browsers.
                // That is why we use require to load the node modules for node environments.

                const https = require("https") as typeof import("https");

                createClientParameters.httpsAgent = new https.Agent({
                    rejectUnauthorized: false,
                });
            }
        }

        this._client = axios.create(createClientParameters);

        if (oauthGrant instanceof OAuthPasswordGrant) {
            this._passwordGrant = oauthGrant;
        } else if (oauthGrant instanceof OAuthRefreshTokenGrant) {
            this._refreshTokenGrant = oauthGrant;
        } else if (oauthGrant instanceof OAuthClientCredentialsGrant) {
            this._clientCredentialsGrant = oauthGrant;
        }

        if (this.shouldAuthenticateWithOAuth) {
            const createRefreshTokenResponseInterceptor = (): void => {
                const responseInterceptor = this._client.interceptors.response.use(
                    (response: any) => response,
                    (error: any) => {
                        // Reject promise if usual error
                        if (error.status !== 401) {
                            return Promise.reject(error);
                        }

                        // When response code is 401, try to refresh the token.
                        // Eject the interceptor so it doesn't loop in case the token refresh causes the 401 response.
                        this._client.interceptors.response.eject(responseInterceptor);

                        return this._client
                            .request({
                                url: "/oauth/token",
                                method: "POST",
                                transformRequest: [
                                    function (data, headers) {
                                        headers["Content-Type"] = "application/x-www-form-urlencoded; charset=UTF-8";
                                        return Object.keys(data)
                                            .reduce((d: Array<string>, k) => {
                                                d.push(`${k}=${encodeURIComponent(data[k])}`);
                                                return d;
                                            }, [])
                                            .join("&");
                                    },
                                ],
                                data: TaJson.serialize(this._refreshTokenGrant),
                            })
                            .then(response => {
                                const tokenInfo = TaJson.deserialize<TokenInfo>(response.data, TokenInfo);
                                this._accessToken = tokenInfo.accessToken;
                                this._refreshTokenGrant!.refreshToken = tokenInfo.refreshToken;

                                error.response.config.headers["Authorization"] = "Bearer " + tokenInfo.accessToken;
                                return axios(error.response.config);
                            })
                            .catch(error => {
                                this._isAuthenticated = false;
                                throw error;
                            })
                            .finally(createRefreshTokenResponseInterceptor);
                    }
                );
            };
            createRefreshTokenResponseInterceptor.bind(this);
            createRefreshTokenResponseInterceptor();
        } else {
            // No OAuthGrant specified, so we must be in the browser.
            this._client.defaults.withCredentials = true; // Send the cookies with the requests.
            this._isAuthenticated = true; // Let's assume we're already authenticated
        }
    }

    async authenticateAsync(): Promise<boolean> {
        if (this._isAuthenticated || !this.shouldAuthenticateWithOAuth) {
            return true;
        }
        if (this._accessToken) {
            return true;
        }

        const grant = this._passwordGrant || this._clientCredentialsGrant;
        if (!grant) {
            throw new InvalidOperationError("Can not authenticate - Missing OAuth grant.");
        }

        return await this._client
            .request({
                url: "/oauth/token",
                method: "POST",
                transformRequest: [
                    function (data, headers) {
                        headers["Content-Type"] = "application/x-www-form-urlencoded; charset=UTF-8";
                        return Object.keys(data)
                            .reduce((d: Array<string>, k) => {
                                d.push(`${k}=${encodeURIComponent(data[k])}`);
                                return d;
                            }, [])
                            .join("&");
                    },
                ],
                data: TaJson.serialize(grant),
            })
            .then(response => {
                const tokenInfo = TaJson.deserialize<TokenInfo>(response.data, TokenInfo);
                this._accessToken = tokenInfo.accessToken;
                this._refreshTokenGrant = new OAuthRefreshTokenGrant(
                    grant.clientId,
                    grant.clientSecret,
                    tokenInfo.refreshToken
                );

                this._client.interceptors.request.use(
                    config => {
                        config.headers["Authorization"] = `Bearer ${this._accessToken}`;
                        return config;
                    },
                    error => {
                        return Promise.reject(error);
                    }
                );

                this._isAuthenticated = true;
                return true;
            })
            .catch(error => {
                this._isAuthenticated = false;
                throw new AuthenticationError("Authentication failed.", error);
            });
    }

    setRequestHeaders(headers: MapStringTo<string>): void {
        Guard.notNullOrUndefined(headers);

        this._requestHeaders = headers;
    }

    /**
     * Sets the provided error handler as an interceptor for the responses.
     * @param onError - the callback that should be called on error.
     */
    setResponseErrorHandler(onError: (error: any) => Promise<any>): void {
        if (this.shouldAuthenticateWithOAuth) {
            throw new InvalidOperationError("Unable to set response error handler when authenticating via OAuth.");
        }

        this._client.interceptors.response.use(
            response => response,
            error => onError(error)
        );
    }

    private send_onFulfilled<T, K>(
        requestMsg: RequestMessage<T>
    ): (value: AxiosResponse<any>) => ResponseMessage<K> | PromiseLike<ResponseMessage<K>> {
        return (response: AxiosResponse<any>) => {
            return ResponseMessage.build<K>(response.status, response.headers as MapStringTo<string>, {
                requestMessage: Object.freeze(requestMsg),
                content: response.data,
                statusText: response.statusText,
            });
        };
    }

    private send_onRejected<T>(error: AxiosError<any>): ResponseMessage<T> {
        if (error.response) {
            return ResponseMessage.build(error.response.status, error.response.headers as MapStringTo<string>, {
                statusText: error.response.statusText,
                content: error.response.data,
            });
        } else if (error.request) {
            return ResponseMessage.build(
                error.request.status,
                {},
                { statusText: error.request.statusText, content: error.request.data }
            );
        } else {
            throw error;
        }
    }

    async sendAsync<T = any, K = any>(
        requestGenerator: () => RequestMessage<T>,
        cancelCallback?: CancelCallback
    ): Promise<ResponseMessage<K>> {
        if (!this._isAuthenticated) {
            throw new InvalidOperationError("Client is not authenticated. Authenticate first.");
        }

        Guard.notNullOrUndefined(requestGenerator);
        const requestMsg = requestGenerator();

        Guard.notNullOrUndefined(requestMsg.requestUri);
        const requestUrl =
            typeof requestMsg.requestUri === "string" ? requestMsg.requestUri : requestMsg.requestUri.href();
        Guard.stringNotNullOrEmpty(requestUrl);

        const requestConfig: AxiosRequestConfig = {};
        requestConfig.headers = { ...this._requestHeaders, ...requestMsg.requestHeaders };

        if (cancelCallback) {
            requestConfig.cancelToken = new axios.CancelToken(cancelCallback);
        }

        let result: ResponseMessage<K> = ResponseMessage.build(0); // Not a valid status code, but we'll know it failed.
        switch (requestMsg.method) {
            case HttpMethod.DELETE:
                result = await this._client
                    .delete(requestUrl, requestConfig)
                    .then(this.send_onFulfilled<T, K>(requestMsg), error => this.send_onRejected<K>(error));
                break;
            case HttpMethod.GET:
                result = await this._client
                    .get(requestUrl, requestConfig)
                    .then(this.send_onFulfilled<T, K>(requestMsg), error => this.send_onRejected<K>(error));
                break;
            case HttpMethod.HEAD:
                result = await this._client
                    .head(requestUrl, requestConfig)
                    .then(this.send_onFulfilled<T, K>(requestMsg), error => this.send_onRejected<K>(error));
                break;
            case HttpMethod.OPTIONS:
                requestConfig.method = "OPTIONS";
                requestConfig.url = requestUrl;
                result = await this._client
                    .request(requestConfig)
                    .then(this.send_onFulfilled<T, K>(requestMsg), error => this.send_onRejected<K>(error));
                break;
            case HttpMethod.PATCH:
                result = await this._client
                    .patch(
                        requestUrl,
                        typeof requestMsg.content === "string"
                            ? requestMsg.content
                            : TaJson.serialize(requestMsg.content),
                        requestConfig
                    )
                    .then(this.send_onFulfilled<T, K>(requestMsg), error => this.send_onRejected<K>(error));
                break;
            case HttpMethod.POST:
                result = await this._client
                    .post(
                        requestUrl,
                        typeof requestMsg.content === "string"
                            ? requestMsg.content
                            : TaJson.serialize(requestMsg.content),
                        requestConfig
                    )
                    .then(this.send_onFulfilled<T, K>(requestMsg), error => this.send_onRejected<K>(error));
                break;
            case HttpMethod.PUT:
                result = await this._client
                    .put(
                        requestUrl,
                        typeof requestMsg.content === "string"
                            ? requestMsg.content
                            : TaJson.serialize(requestMsg.content),
                        requestConfig
                    )
                    .then(this.send_onFulfilled<T, K>(requestMsg), error => this.send_onRejected<K>(error));
                break;
            default:
                throw "Method not supported.";
        }

        return result;
    }
}
